import { PathRoute, useHistory, useLocation } from "@pomle/react-router-paths";
import { Sticky, ViewStack } from "@pomle/react-viewstack";
import { isActiveSession, markAsActiveSession } from "lib/activeSession";
import { useCallback, useMemo } from "react";
import { FlowFrame } from "render/ui/layout/FlowFrame/FlowFrame";
import { StepEntry } from "./components/StepEntry";
import { Transition } from "./components/Transition";
import { isCheckInUrl } from "./isCheckInUrl";
import { findStep, steps } from "./steps";
import { useStepBack } from "./useStepBack";

export function StepFlow() {
  const { pathname, searchParams } = useLocation();
  const { push, replace } = useHistory();

  const currentIndex = useMemo(() => {
    const currentStep = findStep(pathname);
    if (currentStep) {
      return steps.indexOf(currentStep);
    }
    return 0;
  }, [pathname]);

  const resumedFromActiveSessions = isActiveSession(searchParams);

  const resolve = useCallback(
    (url: string) =>
      resumedFromActiveSessions && isCheckInUrl(url)
        ? markAsActiveSession(url)
        : url,
    [resumedFromActiveSessions]
  );

  const goTo = useCallback(
    (url: string) => {
      push(resolve(url));
    },
    [push, resolve]
  );

  // Same navigation, but without a back-stack entry, for steps that must not be
  // reachable again once they have run their side effect.
  const replaceWith = useCallback(
    (url: string) => {
      replace(resolve(url));
    },
    [replace, resolve]
  );

  const backFrom = useStepBack();

  return (
    <FlowFrame>
      <ViewStack>
        {steps.map(({ path, component }, stepIndex) => {
          const offset = stepIndex - currentIndex;

          return (
            <Transition offset={offset} key={path.name}>
              <PathRoute path={path}>
                {(match) => {
                  return (
                    <StepEntry matched={match != null}>
                      <Sticky>
                        {match
                          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            component(match.params as any, {
                              goTo: goTo,
                              replaceWith: replaceWith,
                              onBack: backFrom(path),
                            })
                          : null}
                      </Sticky>
                    </StepEntry>
                  );
                }}
              </PathRoute>
            </Transition>
          );
        })}
      </ViewStack>
    </FlowFrame>
  );
}
